import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { saveAs } from "file-saver";

import { useTable, TableProvider } from "../../../providers/tableProvider";
import { useAlert } from "../../../providers/alertsProvider";
import { useLoader } from "../../../providers/loaderProvider";
import TablePaginated from "../../core/TablePaginated";
import TableCellActions from "../../core/TableCellActions";
import Filter from "../../core/Filter";

import { fecthQcs } from "../../../actions/spotControl/scQcsAction";
import { getDownloadFile } from "../../../helpers/handleDownloadFile";

function TableComposition () {
    let table = useTable();
    const scQcs = useSelector(state => state.scQcs);
    const dispatch = useDispatch();
    const { addError } = useAlert();
    const { showLoader, hideLoader } = useLoader();
    const { t } = useTranslation();
    
    React.useEffect(() => {
      loadQcs();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table.filters]);

    React.useEffect(() => {
      if (scQcs.error){
        addError(scQcs.error.error, scQcs.error.message);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scQcs.error]);

    const loadQcs = () => {
      dispatch(fecthQcs(table.filters));
    }

    const onLogsClick = (qcId) => {
      const url = `/sc2/qcs/${qcId}/logs`;
      showLoader(t('sc2Ps.qcs.actions.downloadLogsLoader'));
      getDownloadFile(url)
        .then(response => {
          saveAs(response.data, `logs_sc2_qc_${qcId}.zip`);
          hideLoader();
        })
        .catch(error => {
          addError(t('sc2Ps.qcs.actions.errorDownloadLogs'), `${t('sc2Ps.qcs.actions.errorMsgDownloadLogs')} ${qcId}`);
          hideLoader();
        });
    };

    const onImagesClick = (qcId) => {
      const url = `/sc2/qcs/${qcId}/images`;
      showLoader(t('sc2Ps.qcs.actions.downloadImagesLoader'));
      getDownloadFile(url)
        .then(response => {
          saveAs(response.data, `images_sc2_qc_${qcId}.zip`);
          hideLoader();
        })
        .catch(error => {
          addError(t('sc2Ps.qcs.actions.errorDownloadImages'), `${t('sc2Ps.qcs.actions.errorMsgDownloadImages')} ${qcId}`);
          hideLoader();
        });
    };

    const getActions = (disabledLogs, disabledImages) => {
      return [
        { key: 'logs', label: t('sc2Ps.qcs.actions.downloadLogs'), icon: 'file alternate outline', handleClick: onLogsClick, disabled: disabledLogs },
        { key: 'images', label: t('sc2Ps.qcs.actions.downloadImages'), icon: 'image outline', handleClick: onImagesClick, disabled: disabledImages }
      ];
    }

    const filters = [
      { key: 1, text: t('sc2Ps.qcs.filter.serialNumber'), value: 'serialNumber', field: 'serialNumber', placeholder: '0010001', type: 'input' },
      { key: 2, text: t('sc2Ps.qcs.filter.producer'), value: 'producer', field: 'producer', placeholder: t('sc2Ps.qcs.filter.producerPlaceholder'), type: 'input' },
      { key: 3, text: t('sc2Ps.qcs.filter.result'), value: 'result', field: 'result', placeholder: t('sc2Ps.qcs.filter.resultPlaceholder'), type: 'select',  options: [{ key: 1, text: 'Success', value: true }, { key: 2, text: 'Fail', value: false }] },
      { key: 4, text: t('sc2Ps.qcs.filter.qcDateBefore'), value: 'timestampBefore', field: 'timestamp', type: 'dateBefore' },
      { key: 5, text: t('sc2Ps.qcs.filter.qcDateAfter'), value: 'timestampAfter', field: 'timestamp', type: 'dateAfter' },
      { key: 6, text: t('sc2Ps.qcs.filter.qcDateBetween'), value: 'timestamp', field: 'timestamp', type: 'dateBetween'}
    ];

    const headerRow = [
      t('sc2Ps.qcs.table.serialNumber'), 
      t('sc2Ps.qcs.table.date'), 
      t('sc2Ps.qcs.table.producer'), 
      t('sc2Ps.qcs.table.result'), 
      t('common.labels.actions')];

    const renderBodyRow = ({ id, serialNumber, timestamp, producer, result, log, images }, i) => ({
      key: `row-${i}`,
      cells: [
        serialNumber || '-',
        timestamp ? moment(timestamp*1000).format('DD-MM-YYYY HH:mm:ss') : '-',
        producer,
        result ? t('common.labels.success') : t('common.labels.fail'),
        { key: 'actions', as: TableCellActions, actions: getActions(log && log.length > 0 ? false : true, images && images.length > 0 ? false : true), rowId: id }
      ],
    });

    return (
      <div>
          <Filter
            filters={filters}
            onAddFilter={table.onResetFilter}
            onClearFilters={table.onClearFilters}
            loading={scQcs.loading}
          />
          <TablePaginated
            headerRow={headerRow}
            renderBodyRow={renderBodyRow}
            tableData={scQcs.qcs}
            loading={scQcs.loading}
            totalItems={scQcs.totalItems}
            setPagination={table.onPaginationChange}
          />
      </div>
    );
}

const TableScQcs = () => <TableProvider><TableComposition /></TableProvider>

export default TableScQcs;